import React from 'react';
import { syncHistoryWithStore } from 'react-router-redux';
import { Router, Route, browserHistory, Redirect, IndexRedirect, applyRouterMiddleware } from 'react-router';
import useScroll from 'react-router-scroll/lib/useScroll';
import { ALL_PRODUCTS_PREFIX } from 'swag-common/constants/main-swag.constants';
import { instantScrollTop, onUpdateRouter } from 'swag-client-common/utils/tricks';
import { APP_ROUTES } from 'swag-client-common/constants/app-routes';
import Root from '../containers/root';
import { ProfileContainer } from '../containers/profile';
import { ProfileSettings } from '../components/routes/account/profile-settings';
import NotFound from '../components/routes/client/not-found';
import { requiredToBeLoggedIn, onAccountOrders, onCompanyOrders, startVerifyingEmail, onDeliveryEnter, onCheckoutEnter, onSetPassword, onProductLeave, onProductEnter, fetchInitialStates, onCategoriesEnter, onProfileSettingsEnter, onCartEnter, onCartLeave, onSearchResultsEnter, onSearchResultsLeave, onCategoriesLeave, onCheckoutLeave, onGiveawayLandingEnter, onShopifyLandingEnter } from './route-hooks/route-hooks';
import { OtherRoutes } from './other-routes';
const LazyProduct = /*#__PURE__*/React.lazy(() => {
  return import( /* webpackChunkName: "[Product]" */'../components/routes/client/product');
});
const LazyCart = /*#__PURE__*/React.lazy(() => {
  return import( /* webpackChunkName: "[Cart]", webpackPrefetch: true */'../components/routes/client/cart');
});
const LazyFavoriteProducts = /*#__PURE__*/React.lazy(() => {
  return import( /* webpackChunkName: "[FavouriteProducts]" */'../components/routes/client/favourite-products');
});
const LazyAccountOrders = /*#__PURE__*/React.lazy(() => {
  return import( /* webpackChunkName: "[AccountOrders]" */'../components/routes/account/orders');
});
const LazyCompanyOrders = /*#__PURE__*/React.lazy(() => {
  return import( /* webpackChunkName: "[CompanyOrders]" */'../components/routes/account/company-orders');
});
const LazyDelivery = /*#__PURE__*/React.lazy(() => {
  return import( /* webpackChunkName: "[Delivery]" */'../containers/delivery');
});
const LazyCheckout = /*#__PURE__*/React.lazy(() => {
  return import( /* webpackChunkName: "[Checkout]" */'../containers/checkout');
});
const LazySearchResults = /*#__PURE__*/React.lazy(() => {
  return import( /* webpackChunkName: "[SearchResults]" */'../components/routes/client/search-results');
});
const LazyProductListing = /*#__PURE__*/React.lazy(() => {
  return import( /* webpackChunkName: "[ProductListing]" */'../components/routes/client/product-listing');
});
const LazyLandingPatagonia = /*#__PURE__*/React.lazy(() => {
  return import( /* webpackChunkName: "[LandingPatagonia]" */'../components/routes/client/landing-patagonia');
});
const LazyLandingGiveaway = /*#__PURE__*/React.lazy(() => {
  return import( /* webpackChunkName: "[LandingGiveaway]" */'../components/routes/client/landing-giveaway').then(({
    LandingGiveaway
  }) => ({
    default: LandingGiveaway
  }));
});
const LazyLandingAutomation = /*#__PURE__*/React.lazy(() => {
  return import( /* webpackChunkName: "[LandingAutomation]" */'../components/routes/client/landing-automation');
});
const LazyLandingZapier = /*#__PURE__*/React.lazy(() => import( /* webpackChunkName: "[LandingZapier]" */'../components/routes/client/landing-zapier').then(({
  LandingZapier
}) => ({
  default: LandingZapier
})));
const LazyLandingShopify = /*#__PURE__*/React.lazy(() => import( /* webpackChunkName: "[LandingShopify]" */'../components/routes/client/landing-shopify').then(({
  LandingShopify
}) => ({
  default: LandingShopify
})));
const LazyLandingExplorer = /*#__PURE__*/React.lazy(() => {
  return import( /* webpackChunkName: "[LandingExplorer]" */'../components/routes/client/landing-explorer').then(({
    LandingExplorer
  }) => ({
    default: LandingExplorer
  }));
});
export function customerRoutes(store, dispatch) {
  const history = syncHistoryWithStore(browserHistory, store);
  const otherRoutes = OtherRoutes({
    store,
    dispatch
  });
  return /*#__PURE__*/React.createElement(Router, {
    history: history,
    render: applyRouterMiddleware(useScroll((prevRouterProps, {
      location
    }) => {
      const scrollRoutes = [APP_ROUTES.COLLECTIONS, APP_ROUTES.KICKSTARTER, APP_ROUTES.SEARCH_RESULTS];
      const isPathCollections = scrollRoutes.some(r => location.pathname.includes(r));
      const wasPathProduct = prevRouterProps && prevRouterProps.location.pathname.slice(1, 8) === 'product';
      return isPathCollections && wasPathProduct;
    })),
    onUpdate: onUpdateRouter(store, dispatch)
  }, /*#__PURE__*/React.createElement(Route, {
    path: APP_ROUTES.HOME,
    component: Root,
    onEnter: fetchInitialStates(store, dispatch)
  }, otherRoutes, /*#__PURE__*/React.createElement(Redirect, {
    from: APP_ROUTES.COLLECTIONS,
    to: APP_ROUTES.ALL_SWAG
  }), /*#__PURE__*/React.createElement(Route, {
    path: `${APP_ROUTES.COLLECTIONS}/:categorySlug`,
    onEnter: onCategoriesEnter(store, dispatch),
    onLeave: onCategoriesLeave(store, dispatch),
    component: props => /*#__PURE__*/React.createElement(LazyProductListing, props)
  }), /*#__PURE__*/React.createElement(Route, {
    path: `${APP_ROUTES.COLLECTIONS}/:categorySlug/${ALL_PRODUCTS_PREFIX}`,
    onEnter: onCategoriesEnter(store, dispatch),
    onLeave: onCategoriesLeave(store, dispatch),
    component: props => /*#__PURE__*/React.createElement(LazyProductListing, props)
  }), /*#__PURE__*/React.createElement(Route, {
    path: `${APP_ROUTES.PRODUCT}/:seoSlug`,
    component: props => /*#__PURE__*/React.createElement(LazyProduct, props),
    onEnter: onProductEnter(store, dispatch),
    onLeave: onProductLeave(store, dispatch)
  }), /*#__PURE__*/React.createElement(Route, {
    path: APP_ROUTES.CART,
    component: props => /*#__PURE__*/React.createElement(LazyCart, props),
    onEnter: onCartEnter(store, dispatch),
    onLeave: onCartLeave(store, dispatch)
  }), /*#__PURE__*/React.createElement(Route, {
    path: APP_ROUTES.DELIVERY,
    component: props => /*#__PURE__*/React.createElement(LazyDelivery, props),
    onEnter: onDeliveryEnter(store, dispatch)
  }), /*#__PURE__*/React.createElement(Route, {
    path: APP_ROUTES.CHECKOUT,
    component: props => /*#__PURE__*/React.createElement(LazyCheckout, props),
    onEnter: onCheckoutEnter(store, dispatch),
    onLeave: onCheckoutLeave(store, dispatch)
  }), /*#__PURE__*/React.createElement(Route, {
    path: APP_ROUTES.SEARCH_RESULTS,
    component: props => /*#__PURE__*/React.createElement(LazySearchResults, props),
    onEnter: onSearchResultsEnter(store, dispatch),
    onLeave: onSearchResultsLeave(store, dispatch)
  }), /*#__PURE__*/React.createElement(Route, {
    path: APP_ROUTES.FAVORITE_PRODUCTS,
    component: props => /*#__PURE__*/React.createElement(LazyFavoriteProducts, props),
    onEnter: instantScrollTop
  }), /*#__PURE__*/React.createElement(Route, {
    path: APP_ROUTES.PATAGONIA,
    component: props => /*#__PURE__*/React.createElement(LazyLandingPatagonia, props),
    onEnter: instantScrollTop
  }), /*#__PURE__*/React.createElement(Route, {
    path: APP_ROUTES.GIVEAWAY,
    component: props => /*#__PURE__*/React.createElement(LazyLandingGiveaway, props),
    onEnter: onGiveawayLandingEnter(store, dispatch)
  }), /*#__PURE__*/React.createElement(Redirect, {
    from: "/giveaway",
    to: APP_ROUTES.GIVEAWAY
  }), /*#__PURE__*/React.createElement(Route, {
    path: APP_ROUTES.AUTOMATION,
    component: props => /*#__PURE__*/React.createElement(LazyLandingAutomation, props),
    onEnter: instantScrollTop
  }), /*#__PURE__*/React.createElement(Route, {
    path: APP_ROUTES.ZAPIER,
    component: props => /*#__PURE__*/React.createElement(LazyLandingZapier, props),
    onEnter: instantScrollTop
  }), /*#__PURE__*/React.createElement(Route, {
    path: APP_ROUTES.SHOPIFY,
    component: props => /*#__PURE__*/React.createElement(LazyLandingShopify, props),
    onEnter: onShopifyLandingEnter(store, dispatch)
  }), /*#__PURE__*/React.createElement(Route, {
    path: APP_ROUTES.EXPLORER,
    component: props => /*#__PURE__*/React.createElement(LazyLandingExplorer, props),
    onEnter: instantScrollTop
  }), /*#__PURE__*/React.createElement(Route, {
    path: `${APP_ROUTES.VERIFY_EMAIL}/:token`,
    onEnter: startVerifyingEmail(store, dispatch)
  }), /*#__PURE__*/React.createElement(Route, {
    path: `${APP_ROUTES.SET_PASSWORD}/:token`,
    onEnter: onSetPassword(store, dispatch, {
      mode: 'setPassword'
    })
  }), /*#__PURE__*/React.createElement(Route, {
    path: `${APP_ROUTES.FORGOT_PASSWORD}/:token`,
    onEnter: onSetPassword(store, dispatch, {
      mode: 'forgotPassword'
    })
  }), /*#__PURE__*/React.createElement(Redirect, {
    from: APP_ROUTES.ACCOUNT,
    to: APP_ROUTES.ACCOUNT_PROFILE
  }), /*#__PURE__*/React.createElement(Route, {
    path: APP_ROUTES.ACCOUNT_ORDERS,
    component: props => /*#__PURE__*/React.createElement(LazyAccountOrders, props),
    onEnter: onAccountOrders(store, dispatch)
  }), /*#__PURE__*/React.createElement(Route, {
    path: APP_ROUTES.COMPANY_ORDERS,
    component: props => /*#__PURE__*/React.createElement(LazyCompanyOrders, props),
    onEnter: onCompanyOrders(store, dispatch)
  }), /*#__PURE__*/React.createElement(Route, {
    path: APP_ROUTES.ACCOUNT_PROFILE,
    component: ProfileContainer,
    onEnter: requiredToBeLoggedIn(store)
  }, /*#__PURE__*/React.createElement(IndexRedirect, {
    to: "settings"
  }), /*#__PURE__*/React.createElement(Route, {
    path: "settings",
    component: ProfileSettings,
    onEnter: onProfileSettingsEnter(store, dispatch)
  })), /*#__PURE__*/React.createElement(Route, {
    path: APP_ROUTES.NOT_FOUND,
    component: NotFound
  }), /*#__PURE__*/React.createElement(Route, {
    path: "*",
    component: NotFound
  })));
}